/**
 * Portal vs admin app origins (dual-subdomain deployments).
 *
 * Examples:
 * - https://glasshousedance.portal.beclassmanager.com
 * - https://glasshousedance.admin.beclassmanager.com
 * - https://test.portal.beclassmanager.com
 *
 * Legacy (still supported):
 * - https://glasshousedance-portal.beclassmanager.com
 * - https://test-admin.beclassmanager.com
 */

export function isAdminHostname(host: string): boolean {
  const h = host.toLowerCase().split(":")[0] ?? "";
  return (
    h.includes(".admin.") ||
    h.startsWith("admin.") ||
    h.includes("-admin") ||
    h.includes("test-admin")
  );
}

export function isPortalHostname(host: string): boolean {
  const h = host.toLowerCase().split(":")[0] ?? "";
  return (
    h.includes(".portal.") ||
    h.startsWith("portal.") ||
    h.includes("-portal") ||
    h.includes("test-portal")
  );
}

/**
 * 403 page may reuse the admin dashboard chrome (sidebar, top bar, notifications)
 * only when the user has an admin-side role AND the request is not on a portal host.
 * Portal hosts must never render admin chrome, even for dual-role users.
 */
export function shouldUseAdminForbiddenShell(
  hasAdminRole: boolean,
  hostname: string,
): boolean {
  return hasAdminRole && !isPortalHostname(hostname);
}

/** Strip paths from NEXTAUTH_URL / NEXTAUTHADMIN_URL env values. */
export function normalizeAppOrigin(raw: string | undefined | null): string | null {
  const value = raw?.trim();
  if (!value) return null;

  try {
    const withProtocol = value.includes("://") ? value : `https://${value}`;
    const parsed = new URL(withProtocol);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return null;
  }
}

export function getConfiguredPortalOrigin(): string | null {
  return normalizeAppOrigin(
    process.env.NEXT_PUBLIC_PORTAL_APP_URL ?? process.env.NEXTAUTH_URL,
  );
}

export function getConfiguredAdminOrigin(): string | null {
  return normalizeAppOrigin(
    process.env.NEXT_PUBLIC_ADMIN_APP_URL ?? process.env.NEXTAUTHADMIN_URL,
  );
}

export function isAdminPath(pathname: string): boolean {
  return pathname.startsWith("/admin");
}

export function isPortalPath(pathname: string): boolean {
  return pathname.startsWith("/portal");
}

/**
 * Origin for auth pages and post-login redirects for a given app path.
 * Prefers the current browser/request host when it matches the path type.
 */
export function getAuthOriginForPath(
  pathname: string,
  hostname?: string,
  currentOrigin?: string,
): string {
  const adminPath = isAdminPath(pathname);
  const portalPath = isPortalPath(pathname);

  if (hostname) {
    const host = hostname.toLowerCase();
    if (adminPath && isAdminHostname(host) && currentOrigin) {
      return normalizeAppOrigin(currentOrigin) ?? currentOrigin;
    }
    if (portalPath && isPortalHostname(host) && currentOrigin) {
      return normalizeAppOrigin(currentOrigin) ?? currentOrigin;
    }
    if (adminPath && isAdminHostname(host)) {
      return `https://${host}`;
    }
    if (portalPath && isPortalHostname(host)) {
      return `https://${host}`;
    }
  }

  if (adminPath) {
    return (
      getConfiguredAdminOrigin() ??
      getConfiguredPortalOrigin() ??
      normalizeAppOrigin(currentOrigin) ??
      ""
    );
  }

  return (
    getConfiguredPortalOrigin() ??
    normalizeAppOrigin(currentOrigin) ??
    ""
  );
}

export function buildAbsoluteAppUrl(
  pathname: string,
  options?: {
    search?: string;
    hostname?: string;
    origin?: string;
  },
): string {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const search = options?.search ?? "";
  const origin = getAuthOriginForPath(path, options?.hostname, options?.origin);

  if (!origin) {
    return `${path}${search}`;
  }

  return `${origin.replace(/\/$/, "")}${path}${search}`;
}
